import { BaseDialog, Button, buttonStylesGhost, ClassNamesFromIStyles, useClassNames, useTheme } from '@h2oai/ui-kit';
import { ChartData } from 'chart.js';
import React, { useState } from 'react';

import BarChart from '../../BarChart/BarChart';
import DataTable from '../../DataTable/DataTable';
import { getH2ogpteStatsTableColumns } from '../H2OGPTe.columns';
import { gpteBarChartStyles, gptePlotStylesDefault, gptePlotTableStyles, IGPTePlotStyles } from './GPTePlot.styles';

interface IGPTePlotProps {
  title: string;
  yAxisLabel?: string;
  data: ChartData<'bar'>;
  tableColumnHeader: string;
  loading?: boolean;
  error?: boolean;
}

const NUMBER_OF_BARS = 6;

const GPTePlot = ({ title, yAxisLabel, data, tableColumnHeader, loading, error }: IGPTePlotProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const theme = useTheme();
  const classNames = useClassNames<IGPTePlotStyles, ClassNamesFromIStyles<IGPTePlotStyles>>(
    'gpte-plot',
    gptePlotStylesDefault
  );

  const isEmpty =
    !data ||
    !Array.isArray(data.labels) ||
    data.labels.length === 0 ||
    !data.datasets?.[0] ||
    !data.datasets[0].data?.length;

  const chartDataToKeyValueList = (data: ChartData<'bar'>): { key: string; value: number }[] => {
    if (!data || !data.labels || !data.datasets?.[0]) return [];

    const dataset = data.datasets[0];
    const rawData = dataset.data as number[];

    return data.labels
      .map((label, index) => ({
        key: String(label),
        value: rawData[index] ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const transformedData: ChartData<'bar'> = React.useMemo(() => {
    if (!data || !data.labels || !data.datasets?.[0]) return data;

    const dataset = data.datasets[0];
    const rawData = dataset.data as number[];

    const labelValuePairs = data.labels.map((label, index) => ({
      label,
      value: rawData[index] ?? 0,
    }));

    const highestValues = labelValuePairs.sort((a, b) => b.value - a.value).slice(0, NUMBER_OF_BARS);

    return {
      labels: highestValues.map((item) => item.label),
      datasets: [
        {
          ...dataset,
          data: highestValues.map((item) => item.value),
        },
      ],
    };
  }, [data]);

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false, position: 'top' as const },
      title: { display: false, text: title },
    },
    scales: {
      x: { stacked: false },
      y: { stacked: false, title: { display: Boolean(yAxisLabel), text: yAxisLabel } },
    },
  };

  return (
    <div className={classNames.root}>
      <div className={classNames.header}>
        <h2>{title}</h2>
        <Button
          styles={buttonStylesGhost}
          text="Show All"
          onClick={() => {
            setIsDialogOpen(true);
          }}
          disabled={isEmpty}
        />
      </div>
      <BarChart
        data={transformedData}
        options={options}
        barChartStyles={gpteBarChartStyles}
        loading={loading}
        error={error}
      />
      <BaseDialog
        hidden={!isDialogOpen}
        onDismiss={() => {
          setIsDialogOpen(!isDialogOpen);
        }}
        title={title}
        content={
          <div className={classNames.dialogContentRoot}>
            <DataTable
              items={chartDataToKeyValueList(data)}
              columns={getH2ogpteStatsTableColumns(tableColumnHeader)}
              searchFields={[]}
              contentHeight={300}
              styles={gptePlotTableStyles(theme)}
            />
          </div>
        }
      />
    </div>
  );
};

export default GPTePlot;
